import type {
  ListMembersQueryInput,
  ListProjectsQueryInput,
  ListTasksQueryInput,
} from "@tm/contracts";

const taskDetailKey = (workspaceId: string, projectId: string, taskId: string) =>
  ["workspaces", workspaceId, "projects", "tasks", projectId, "detail", taskId] as const;

/**
 * Central TanStack Query keys. Keys are hierarchical so invalidating a prefix
 * (e.g. `projectPeople(workspaceId, projectId)`) also refreshes everything beneath it.
 */
export const queryKeys = {
  me: ["auth", "me"] as const,
  sessions: ["auth", "sessions"] as const,
  myInvitations: ["auth", "invitations"] as const,
  /** Whether the server has Web Push turned on (its VAPID public key). */
  pushConfig: ["auth", "push", "config"] as const,
  /** This browser's push subscription endpoint (browser state, read through the Push API). */
  pushSubscription: ["auth", "push", "subscription"] as const,
  workspaces: ["workspaces"] as const,
  members: (workspaceId: string) => ["workspaces", workspaceId, "members"] as const,
  memberList: (
    workspaceId: string,
    filters: Pick<ListMembersQueryInput, "search" | "role" | "limit">,
  ) => ["workspaces", workspaceId, "members", filters] as const,
  invitations: (workspaceId: string) => ["workspaces", workspaceId, "invitations"] as const,
  /** Teams and members of one project; invalidating the prefix refreshes both. */
  projectPeople: (workspaceId: string, projectId: string) =>
    ["workspaces", workspaceId, "projects", "people", projectId] as const,
  teamList: (workspaceId: string, projectId: string) =>
    ["workspaces", workspaceId, "projects", "people", projectId, "teams", "list"] as const,
  team: (workspaceId: string, projectId: string, teamId: string) =>
    [
      "workspaces",
      workspaceId,
      "projects",
      "people",
      projectId,
      "teams",
      "detail",
      teamId,
    ] as const,
  projectMembers: (workspaceId: string, projectId: string) =>
    ["workspaces", workspaceId, "projects", "people", projectId, "members"] as const,
  projects: (workspaceId: string) => ["workspaces", workspaceId, "projects"] as const,
  projectLists: (workspaceId: string) => ["workspaces", workspaceId, "projects", "list"] as const,
  projectList: (workspaceId: string, filters: Pick<ListProjectsQueryInput, "status" | "mine">) =>
    ["workspaces", workspaceId, "projects", "list", filters] as const,
  project: (workspaceId: string, projectKey: string) =>
    ["workspaces", workspaceId, "projects", "detail", projectKey] as const,
  /** Everything task-related in one project (statuses, lists, workload). */
  projectTasks: (workspaceId: string, projectId: string) =>
    ["workspaces", workspaceId, "projects", "tasks", projectId] as const,
  taskStatuses: (workspaceId: string, projectId: string) =>
    ["workspaces", workspaceId, "projects", "tasks", projectId, "statuses"] as const,
  taskLists: (workspaceId: string, projectId: string) =>
    ["workspaces", workspaceId, "projects", "tasks", projectId, "list"] as const,
  taskList: (
    workspaceId: string,
    projectId: string,
    filters: Pick<ListTasksQueryInput, "assigneeId" | "search" | "parentTaskId" | "tagIds">,
  ) => ["workspaces", workspaceId, "projects", "tasks", projectId, "list", filters] as const,
  /** One task's own data (detail, comments, activity, ...); nested under `projectTasks`. */
  taskDetail: taskDetailKey,
  /** Resolves a `KEY-12` from the URL to a task id. */
  taskLookup: (workspaceId: string, projectId: string, taskKey: string) =>
    ["workspaces", workspaceId, "task-lookup", projectId, taskKey] as const,
  taskComments: (workspaceId: string, projectId: string, taskId: string) =>
    [...taskDetailKey(workspaceId, projectId, taskId), "comments"] as const,
  taskActivity: (workspaceId: string, projectId: string, taskId: string) =>
    [...taskDetailKey(workspaceId, projectId, taskId), "activity"] as const,
  taskChecklists: (workspaceId: string, projectId: string, taskId: string) =>
    [...taskDetailKey(workspaceId, projectId, taskId), "checklists"] as const,
  taskWatchers: (workspaceId: string, projectId: string, taskId: string) =>
    [...taskDetailKey(workspaceId, projectId, taskId), "watchers"] as const,
  taskDependencies: (workspaceId: string, projectId: string, taskId: string) =>
    [...taskDetailKey(workspaceId, projectId, taskId), "dependencies"] as const,
  /** The workspace's tag definitions. */
  tags: (workspaceId: string) => ["workspaces", workspaceId, "tags"] as const,
  workload: (workspaceId: string, projectId: string) =>
    ["workspaces", workspaceId, "projects", "tasks", projectId, "workload"] as const,
  /** The caller's open tasks across projects; task mutations invalidate it. */
  myWork: (workspaceId: string) => ["workspaces", workspaceId, "my-work"] as const,
  /** Everything chat-related in one workspace. */
  chat: (workspaceId: string) => ["workspaces", workspaceId, "chat"] as const,
  channels: (workspaceId: string) => ["workspaces", workspaceId, "chat", "channels"] as const,
  browseChannels: (workspaceId: string) => ["workspaces", workspaceId, "chat", "browse"] as const,
  channelMembers: (workspaceId: string, channelId: string) =>
    ["workspaces", workspaceId, "chat", "members", channelId] as const,
  messages: (workspaceId: string, channelId: string) =>
    ["workspaces", workspaceId, "chat", "messages", channelId] as const,
  /** Replies of one thread; nested under `messages` so channel-wide invalidation covers it. */
  threadMessages: (workspaceId: string, channelId: string, parentMessageId: string) =>
    ["workspaces", workspaceId, "chat", "messages", channelId, "thread", parentMessageId] as const,
  pins: (workspaceId: string, channelId: string) =>
    ["workspaces", workspaceId, "chat", "pins", channelId] as const,
  searchMessages: (workspaceId: string, text: string) =>
    ["workspaces", workspaceId, "chat", "search", text] as const,
  presence: (workspaceId: string) => ["workspaces", workspaceId, "chat", "presence"] as const,
  /** Everything notification-related in one workspace (the caller's own inbox). */
  notifications: (workspaceId: string) => ["workspaces", workspaceId, "notifications"] as const,
  notificationList: (workspaceId: string, unreadOnly: boolean) =>
    ["workspaces", workspaceId, "notifications", "list", { unreadOnly }] as const,
  unreadNotifications: (workspaceId: string) =>
    ["workspaces", workspaceId, "notifications", "unread-count"] as const,
  invitationPreview: (token: string) => ["invitations", token] as const,
};
